/**
 * The shared engagement beacon — ONE implementation for both client-facing surfaces:
 *
 *   - the feasibility report web view: inlined as a classic script by
 *     apps/api/feasibility/webview.py from the committed copy at
 *     apps/api/feasibility/assets/beacon.js (the Modal image only ships the feasibility
 *     package, so it cannot read this file at render time — run `make sync-beacon` after
 *     editing here; a test in apps/api/tests/test_webview.py fails on drift)
 *   - the public quote page: imported as an ES module by
 *     apps/leads-web/src/public/PublicQuotePage.tsx
 *
 * The contract both servers implement (Xano APIs 324 and 334):
 *
 *   POST url  { session_uid, total_ms, max_scroll_pct,
 *               sections: [{key, state, dwell_ms}], events: [{key, count}] }
 *
 * Everything sent is CUMULATIVE, never incremental. The server takes the MAX of stored and
 * incoming, so a lost, duplicated or out-of-order beacon costs nothing — the next one carries
 * the total. Do not "optimise" this into deltas.
 *
 * Dwell is sampled off one clock, and only accrues while a section is on screen AND the tab is
 * visible, with a guard that discards any gap longer than two flush intervals — a slept laptop
 * must not bank hours against whatever section was on screen.
 *
 * Deliberately ES5-style and dependency-free: the report inlines this into a plain <script>,
 * so nothing here may assume a bundler, a polyfill, or module scope.
 *
 * Returns null when no session id was supplied (metrics stay silent; the page must already
 * work without them), otherwise a handle with stop() — required by React StrictMode, whose
 * double-run effects would otherwise leave two intervals both banking time.
 */
export function initEngagementBeacon(opts) {
  var sid = opts.sessionId;
  if (!sid) return null;

  var url = opts.url;
  var flushMs = opts.flushMs || 15000;
  var nodes = [].slice.call(document.querySelectorAll("[data-section-key]"));
  var dwell = {}, visible = {}, events = {}, totalMs = 0, maxScroll = 0;
  var last = Date.now();
  var io = null;

  nodes.forEach(function (el) { dwell[el.getAttribute("data-section-key")] = 0; });

  // Time is accrued by sampling rather than by timers per section: one clock, and a section only
  // accrues while it is on screen AND the tab is visible. A backgrounded tab must not bank hours.
  function tick() {
    var now = Date.now(), delta = now - last;
    last = now;
    if (document.visibilityState !== "visible" || delta < 0) return;
    if (delta > flushMs * 2) return;               // slept or throttled — do not credit the gap
    totalMs += delta;
    for (var k in visible) { if (visible[k]) dwell[k] += delta; }
  }

  if ("IntersectionObserver" in window) {
    io = new IntersectionObserver(function (entries) {
      tick();
      entries.forEach(function (e) {
        visible[e.target.getAttribute("data-section-key")] = e.isIntersecting;
      });
    }, { threshold: 0.25 });
    nodes.forEach(function (el) { io.observe(el); });
  }

  function onScroll() {
    var h = document.documentElement.scrollHeight - window.innerHeight;
    var pct = h > 0 ? ((window.scrollY || 0) / h) * 100 : 100;
    if (pct > maxScroll) maxScroll = pct > 100 ? 100 : pct;
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  function body() {
    return JSON.stringify({
      session_uid: sid,
      total_ms: Math.round(totalMs),
      max_scroll_pct: Math.round(maxScroll),
      sections: nodes.map(function (el) {
        var k = el.getAttribute("data-section-key");
        return { key: k, state: el.getAttribute("data-section-state") || "full",
                 dwell_ms: Math.round(dwell[k]) };
      }),
      events: Object.keys(events).map(function (k) { return { key: k, count: events[k] }; })
    });
  }

  function flush(final) {
    tick();
    var payload = body();
    // sendBeacon survives the page going away; fetch does not, reliably. Content type matters:
    // both servers parse JSON, and a bare sendBeacon string would arrive as text/plain.
    if (final && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
      return;
    }
    fetch(url, { method: "POST", headers: { "content-type": "application/json" },
                 body: payload, keepalive: true }).catch(function () {});
  }

  var intervalId = setInterval(function () { flush(false); }, flushMs);

  function onPageHide() { flush(true); }
  function onVisibilityChange() {
    tick();
    if (document.visibilityState === "hidden") flush(true);
  }
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);

  // First-click-wins events. Both flush immediately — a CTA click is usually the last thing
  // that happens before the reader navigates away. The server treats these as first-click-wins,
  // so the count is always 1.
  function on(selector, key) {
    [].slice.call(document.querySelectorAll(selector)).forEach(function (el) {
      el.addEventListener("click", function () { events[key] = 1; flush(true); });
    });
  }
  (opts.clickEvents || []).forEach(function (ce) { on(ce.selector, ce.key); });

  // Counted outbound link clicks, attributed to the section they happened in.
  //
  // Delegated off the document and resolved with closest(), so the links themselves need no
  // markup of their own — which is what keeps this out of the document builders (and, for the
  // report, out of the PDF). The count is cumulative like everything else here: the server
  // takes the max and moves its counter by the delta, so a re-sent beacon cannot double-count.
  var LINK_EVENTS = opts.linkEvents || {};
  function onDocumentClick(e) {
    var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return;
    // Same-page and mailto hrefs are not outbound content. Anything with its own first-click
    // event (the report's CTA) lives in a section with no link event, so it never reaches this.
    var href = a.getAttribute("href") || "";
    if (href.charAt(0) === "#" || href.indexOf("mailto:") === 0) return;
    var sec = a.closest("[data-section-key]");
    var key = sec && LINK_EVENTS[sec.getAttribute("data-section-key")];
    if (!key) return;
    events[key] = (events[key] || 0) + 1;
    // target="_blank" readers stay here and may click several. No need for the pagehide-grade
    // flush — the next interval carries it, and pagehide is the backstop.
    flush(false);
  }
  document.addEventListener("click", onDocumentClick);

  // Teardown for module consumers (the report page never calls this — it lives for the whole
  // document). Per-element click listeners are left in place: their elements unmount with the
  // page that installed them, and a dead listener writing to a dead `events` map sends nothing.
  function stop() {
    clearInterval(intervalId);
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    document.removeEventListener("click", onDocumentClick);
    if (io) io.disconnect();
  }

  return { flush: flush, stop: stop };
}
